import { Injectable } from '@angular/core';
import {
	CanActivate,
	CanActivateChild,
	CanLoad,
	ActivatedRouteSnapshot,
	Route,
	Router,
	RouterStateSnapshot,
	UrlTree
} from '@angular/router';
import { AuthService } from '../../modules/auth/auth.service';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
	providedIn: 'root'
})
export class AuthGuard implements CanActivate, CanActivateChild, CanLoad {
	constructor(
		private authService: AuthService,
		private router: Router
	) {}

	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
		if (this.isNoAccessRoute(route, state.url)) {
			return of(true);
		}

		const permission = route.data['permission'];

		const roles = route.data['roles'];
		return this.checkAccess(permission, roles ?? []);
	}

	canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
		if (this.isNoAccessRoute(childRoute, state.url)) {
			return of(true);
		}

		const permission = childRoute.data['permission'];
		const roles = childRoute.data['roles'];
		return this.checkAccess(permission, roles ?? []);
	}

	canLoad(route: Route): Observable<boolean | UrlTree> {
		const permission = route.data ? route.data['permission'] : null;
		const roles = route.data ? route.data['roles'] : [];
		return this.checkAccess(permission, roles);
	}

	private checkAccess(permission: string | null, roles: string[]): Observable<boolean | UrlTree> {
		if (!this.authService.isLoggedIn()) {
			const attemptedUrl = this.router.getCurrentNavigation()?.initialUrl.toString();
			if (attemptedUrl && attemptedUrl !== '/' && !attemptedUrl.startsWith('/auth')) {
				sessionStorage.setItem('redirect_after_login', attemptedUrl);
			}
			/**
			 * createUrlTree() is the preferred way to handle navigation in guards because it
			 * 1. returns a UrlTree tells Angular to cancel the current navigation
			 *  and replace it with the new one.
			 * 2. aoids potential issues like infinite loops and side effects.
			 */
			return of(this.router.createUrlTree(['/auth/login']));
		}

		return this.authService.getUserRolesAndPermissions().pipe(
			map((response) => {
				const { permissions, roles: userRoles } = response;
				const safePermissions = Array.isArray(permissions) ? permissions : [];
				const safeRoles = Array.isArray(userRoles) ? userRoles : [];
				const hasAnyAccess = safePermissions.length > 0 || safeRoles.length > 0;
				const hasPermission = permission ? safePermissions.includes(permission) : false;
				/** NOTE:
				 * 1. If no roles are required (roles.length === 0), access is
				 * automatically granted. (roles in some routes haven't  been set up yet)
				 * 2. If roles are required, the user must have at least one matching role.
				 */
				const hasRole = roles.length === 0 || roles.some((role) => safeRoles.includes(role));

				if (!hasAnyAccess) {
					return this.router.createUrlTree(['/no-access']);
				}

				if (hasPermission || hasRole) {
					return true;
				}

				return this.router.createUrlTree(['/no-access']);
			})
		);
	}

	private isNoAccessRoute(route: ActivatedRouteSnapshot, url: string): boolean {
		return route.routeConfig?.path === 'no-access' || url.startsWith('/no-access');
	}
}
